// Taşeron portalı — `supplier-contractor` (Taşeron Yetkilisi) erişim sondası (route guard UX).
//
// İki katmanlı authz (api-contract §17): Taşeron **Yetkilisi** = `supplier-self` +
// `supplier-contractor`; Taşeron **Personeli** = yalnız `supplier-self`. Yetkili-yalnız yönetim
// ekranları (personel listesi + personel belgeleri, §15.2 yol B) yalnız `supplier-contractor`
// grant'lı Yetkili'ye açıktır. Asıl yetki backend'dedir (`[ModuleAction("supplier-contractor",
// …)]`); bu sonda yalnız UX görünürlüğüdür — hafif `GET /supplier/contractor` probu çeker:
//   - 2xx                                  → erişim var (granted) → yönetim menüsü + ekranlar görünür
//   - 404 CONTRACTOR_NOT_FOUND             → grant VAR ama aktif firmada taşeron bağı yok →
//                                            granted (ekranlar görünür; iç sayfa "bağ yok" durumunu açar)
//   - 403 COMPANY_NOT_ACCESSIBLE           → grant VAR ama firmaya atama yok → granted (iç sayfa
//                                            firma seçici yönlendirmesini gösterir)
//   - 403 (diğer / grant yok)              → **Personeli** → forbidden (menü + ekran gizli)
//   - diğer (5xx / ağ)                     → servis kullanılamıyor (unavailable) → yeniden dene
//
// web-expert `useContractorsAccess` (contractorsAccess.ts) deseniyle aynı; supplier tek firmaya
// kapsanmış self-service olduğu için companyId anahtara katılır (firma değişiminde yeniden çeker).

import { ApiError, supplierContractorApi, useAuthStore } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { supplierContractorKeys } from './supplierContractorKeys';

export type SupplierContractorAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface SupplierContractorAccess {
  status: SupplierContractorAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * `supplier-contractor` (Yetkili) erişimini backend sondasıyla belirler. Oturum açık
 * (RequireAuth altı) varsayılır. 4xx'te retry yok; **grant yok (403)** ≠ **bağ yok (404)** ≠
 * **firma erişimi yok (403 COMPANY_NOT_ACCESSIBLE)** ≠ servis (5xx) ayrımı korunur — yalnız
 * grant-yok (Personeli) gizlenir; diğer durumlar iç sayfanın nazik durum ekranına bırakılır.
 */
export function useSupplierContractorAccess(): SupplierContractorAccess {
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);

  const query = useQuery({
    queryKey: supplierContractorKeys.access(companyId),
    queryFn: () => supplierContractorApi.getSupplierContractor(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: SupplierContractorAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 404) {
    // Grant VAR, aktif firmada taşeron bağı yok — ekranlar görünür (iç sayfa "bağ yok" açar).
    status = 'granted';
  } else if (
    query.error instanceof ApiError &&
    query.error.statusCode === 403 &&
    query.error.errorCode === 'COMPANY_NOT_ACCESSIBLE'
  ) {
    // Grant VAR, firmaya atama yok — ekranlar görünür (iç sayfa firma seçiciyi önerir).
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    // Grant YOK → Personeli → gizle.
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
